/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiScreenReaderOnly } from '../../accessibility';
import { htmlIdGenerator, keys } from '../../../services';
import { EuiSuperSelectControl } from './super_select_control';
import { EuiInputPopover } from '../../popover';
import { EuiContextMenuItem } from '../../context_menu';
import { EuiI18n } from '../../i18n';
var ShiftDirection;

(function (ShiftDirection) {
  ShiftDirection["BACK"] = "back";
  ShiftDirection["FORWARD"] = "forward";
})(ShiftDirection || (ShiftDirection = {}));

export class EuiSuperSelect extends Component {
  static defaultProps = {
    hasDividers: false,
    fullWidth: false,
    compressed: false,
    isInvalid: false,
    isLoading: false
  };
  itemNodes = [];
  _isMounted = false;
  describedById = htmlIdGenerator('euiSuperSelect_')('_screenreaderDescribeId');
  labelledById = htmlIdGenerator('euiSuperSelect_')('_screenreaderLabelId');
  state = {
    isPopoverOpen: this.props.isOpen || false
  };

  componentDidMount() {
    this._isMounted = true;

    if (this.props.isOpen) {
      this.openPopover();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setItemNode = (node, index) => {
    this.itemNodes[index] = node;
  };
  openPopover = () => {
    this.setState({
      isPopoverOpen: true
    });

    const focusSelected = () => {
      const indexOfSelected = this.props.options.reduce((indexOfSelected, option, index) => {
        if (indexOfSelected != null) return indexOfSelected;
        if (option == null) return null;
        return option.value === this.props.valueOfSelected ? index : null;
      }, null);
      requestAnimationFrame(() => {
        if (!this._isMounted) {
          return;
        }

        if (this.props.valueOfSelected != null) {
          if (indexOfSelected != null) {
            this.focusItemAt(indexOfSelected);
          } else {
            focusSelected();
          }
        } else {
          const firstFocusableOption = this.props.options.findIndex(({
            disabled
          }) => disabled !== true);
          this.focusItemAt(firstFocusableOption);
        }

        if (this.props.onFocus) {
          this.props.onFocus();
        }
      });
    };

    requestAnimationFrame(focusSelected);
  };
  closePopover = () => {
    this.setState({
      isPopoverOpen: false
    });

    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };
  itemClicked = value => {
    this.closePopover();

    if (this.props.onChange) {
      this.props.onChange(value);
    }
  };
  onSelectKeyDown = event => {
    if (event.key === keys.ARROW_UP || event.key === keys.ARROW_DOWN) {
      event.preventDefault();
      event.stopPropagation();
      this.openPopover();
    }
  };
  onItemKeyDown = event => {
    switch (event.key) {
      case keys.ESCAPE:
        // close the popover and prevent ancestors from handling
        event.preventDefault();
        event.stopPropagation();
        this.closePopover();
        break;

      case keys.TAB:
        // no-op
        event.preventDefault();
        event.stopPropagation();
        break;

      case keys.ARROW_UP:
        event.preventDefault();
        event.stopPropagation();
        this.shiftFocus(ShiftDirection.BACK);
        break;

      case keys.ARROW_DOWN:
        event.preventDefault();
        event.stopPropagation();
        this.shiftFocus(ShiftDirection.FORWARD);
        break;
    }
  };

  focusItemAt(index) {
    const targetElement = this.itemNodes[index];

    if (targetElement != null) {
      targetElement.focus();
    }
  }

  shiftFocus(direction) {
    const currentIndex = this.itemNodes.indexOf(document.activeElement);
    let targetElementIndex;

    if (currentIndex === -1) {
      // somehow the select options has lost focus
      targetElementIndex = 0;
    } else {
      if (direction === ShiftDirection.BACK) {
        targetElementIndex = currentIndex === 0 ? this.itemNodes.length - 1 : currentIndex - 1;
      } else {
        targetElementIndex = currentIndex === this.itemNodes.length - 1 ? 0 : currentIndex + 1;
      }
    }

    this.focusItemAt(targetElementIndex);
  }

  render() {
    const {
      className,
      options,
      valueOfSelected,
      onChange,
      isOpen,
      isInvalid,
      hasDividers,
      itemClassName,
      itemLayoutAlign,
      fullWidth,
      popoverProps,
      compressed,
      ...rest
    } = this.props;
    const popoverClasses = classNames('euiSuperSelect', popoverProps?.className);
    const buttonClasses = classNames({
      'euiSuperSelect--isOpen__button': this.state.isPopoverOpen
    }, className);
    const itemClasses = classNames('euiSuperSelect__item', {
      'euiSuperSelect__item--hasDividers': hasDividers
    }, itemClassName);
    const button = <EuiSuperSelectControl screenReaderId={this.labelledById} options={options} value={valueOfSelected} onClick={this.state.isPopoverOpen ? this.closePopover : this.openPopover} onKeyDown={this.onSelectKeyDown} className={buttonClasses} fullWidth={fullWidth} isInvalid={isInvalid} compressed={compressed} {...rest} />;
    const items = options.map((option, index) => {
      const {
        value,
        dropdownDisplay,
        inputDisplay,
        ...optionRest
      } = option;
      return <EuiContextMenuItem key={index} className={itemClasses} icon={valueOfSelected === value ? 'check' : 'empty'} onClick={() => this.itemClicked(value)} onKeyDown={this.onItemKeyDown} layoutAlign={itemLayoutAlign} buttonRef={node => this.setItemNode(node, index)} role="option" id={value} aria-selected={valueOfSelected === value} {...optionRest}>
          {dropdownDisplay || inputDisplay}
        </EuiContextMenuItem>;
    });
    return <EuiInputPopover closePopover={this.closePopover} panelPaddingSize="none" {...popoverProps} className={popoverClasses} isOpen={isOpen || this.state.isPopoverOpen} input={button} fullWidth={fullWidth}>
        <EuiScreenReaderOnly>
          <p id={this.describedById}>
            <EuiI18n token="euiSuperSelect.screenReaderAnnouncement" default="You are in a form selector and must select a single option.
              Use the up and down keys to navigate or escape to close." />
          </p>
        </EuiScreenReaderOnly>
        <div aria-labelledby={this.labelledById} aria-describedby={this.describedById} className="euiSuperSelect__listbox" role="listbox" aria-activedescendant={valueOfSelected} tabIndex={0}>
          {items}
        </div>
      </EuiInputPopover>;
  }

}
EuiSuperSelect.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  compressed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isLoading: PropTypes.bool,
  readOnly: PropTypes.bool,
  name: PropTypes.string,

  /**
     * Creates an input group with element(s) coming before input.
     * `string` | `ReactElement` or an array of these
     */
  prepend: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),

  /**
     * Creates an input group with element(s) coming after input.
     * `string` | `ReactElement` or an array of these
     */
  append: PropTypes.oneOfType([PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.element.isRequired]).isRequired).isRequired]),

  /**
     * Creates a semantic label ID for the `div[role="listbox"]` that's opened on click or keypress.
     * __Generated and passed down by `EuiSuperSelect`.__
     */
  screenReaderId: PropTypes.string,

  /**
       * Pass an array of options that must at least include:
       * `value`: storing unique value of item,
       * `inputDisplay`: what shows inside the form input when selected
       * `dropdownDisplay` (optional): what shows for the item in the dropdown
       */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.any.isRequired,
    inputDisplay: PropTypes.node,
    dropdownDisplay: PropTypes.node,
    disabled: PropTypes.bool,
    "data-test-subj": PropTypes.string
  }).isRequired).isRequired,
  valueOfSelected: PropTypes.any,

  /**
       * Classes for the context menu item
       */
  itemClassName: PropTypes.string,

  /**
       * You must pass an `onChange` function to handle the update of the value
       */
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,

  /**
       * Change to `true` if you want horizontal lines between options.
       * This is best used when options are multi-line.
       */
  hasDividers: PropTypes.bool,

  /**
       * Change `EuiContextMenuItem` layout position of icon
       */
  itemLayoutAlign: PropTypes.oneOf(["center", "top", "bottom"]),

  /**
       * Controls whether the options are shown. Default: false
       */
  isOpen: PropTypes.bool,

  /**
       * Optional props to pass to the underlying [EuiPopover](/#/layout/popover).
       * Allows fine-grained control of the popover dropdown menu, including
       * `repositionOnScroll` for EuiSuperSelects used within scrollable containers,
       * and customizing popover panel styling.
       *
       * Does not accept a nested `popoverProps.isOpen` property - use the top level
       * `isOpen` API instead.
       */
  popoverProps: PropTypes.any
};

try {
  EuiSuperSelect.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/super_select/super_select.tsx',
    description: '',
    displayName: 'EuiSuperSelect',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      readOnly: {
        defaultValue: null,
        description: '',
        name: 'readOnly',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      prepend: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming before input.\n' + '`string` | `ReactElement` or an array of these',
        name: 'prepend',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      append: {
        defaultValue: null,
        description: 'Creates an input group with element(s) coming after input.\n' + '`string` | `ReactElement` or an array of these',
        name: 'append',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'PrependAppendType'
        }
      },
      screenReaderId: {
        defaultValue: null,
        description: 'Creates a semantic label ID for the `div[role="listbox"]` that\'s opened on click or keypress.\n' + '__Generated and passed down by `EuiSuperSelect`.__',
        name: 'screenReaderId',
        parent: {
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select_control.tsx',
          name: 'EuiSuperSelectControlProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      options: {
        defaultValue: null,
        description: 'Pass an array of options that must at least include:\n' + '`value`: storing unique value of item,\n' + '`inputDisplay`: what shows inside the form input when selected\n' + '`dropdownDisplay` (optional): what shows for the item in the dropdown',
        name: 'options',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'EuiSuperSelectOption<T>[]'
        }
      },
      valueOfSelected: {
        defaultValue: null,
        description: '',
        name: 'valueOfSelected',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      itemClassName: {
        defaultValue: null,
        description: 'Classes for the context menu item',
        name: 'itemClassName',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'You must pass an `onChange` function to handle the update of the value',
        name: 'onChange',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(value: T) => void'
        }
      },
      onFocus: {
        defaultValue: null,
        description: '',
        name: 'onFocus',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(event?: FocusEvent<Element, Element>) => void'
        }
      },
      onBlur: {
        defaultValue: null,
        description: '',
        name: 'onBlur',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: '(event?: FocusEvent<Element, Element>) => void'
        }
      },
      hasDividers: {
        defaultValue: {
          value: 'false'
        },
        description: 'Change to `true` if you want horizontal lines between options.\n' + 'This is best used when options are multi-line.',
        name: 'hasDividers',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      itemLayoutAlign: {
        defaultValue: null,
        description: 'Change `EuiContextMenuItem` layout position of icon',
        name: 'itemLayoutAlign',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: 'EuiContextMenuItemLayoutAlignment',
          value: [{
            value: '"center"'
          }, {
            value: '"top"'
          }, {
            value: '"bottom"'
          }]
        }
      },
      isOpen: {
        defaultValue: null,
        description: 'Controls whether the options are shown. Default: false',
        name: 'isOpen',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      popoverProps: {
        defaultValue: null,
        description: 'Optional props to pass to the underlying [EuiPopover](/#/layout/popover).\n' + 'Allows fine-grained control of the popover dropdown menu, including\n' + '`repositionOnScroll` for EuiSuperSelects used within scrollable containers,\n' + 'and customizing popover panel styling.\n' + '\n' + 'Does not accept a nested `popoverProps.isOpen` property - use the top level\n' + '`isOpen` API instead.',
        name: 'popoverProps',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/form/super_select/super_select.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'Partial<CommonProps & Omit<EuiPopoverProps, "isOpen">>'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'ButtonHTMLAttributes', 'EuiSuperSelectControlProps']
  };
} catch (e) {}